import { useEffect, useState } from 'react';
import { PanelBottomOpen, PanelRightOpen, X, Maximize2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { ForeignKeyMap } from '../hooks/useTableMetadata';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { LooseRelationship } from '../../../../types/electron';
import type { DatabaseEngineId } from '../../../../core/engines';
import { RowDetailsDataTab } from './RowDetailsDataTab';
import { RowDetailsRelatedDataTab } from './RowDetailsRelatedDataTab';

type RowDetailsPanelProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly schema: string;
  readonly tableName: string;
  readonly rowIndex: number;
  readonly row: Record<string, unknown>;
  readonly columns: TableExplorerColumn[];
  readonly foreignKeyMap?: ForeignKeyMap;
  readonly layout?: 'bottom' | 'side';
  readonly onSwitchLayout?: (layout: 'bottom' | 'side') => void;
  readonly relatedData: {
    readonly supportsInboundForeignKeyExplorer: boolean;
    readonly inboundForeignKeys: TableExplorerInboundForeignKey[];
    readonly isInboundForeignKeysLoading: boolean;
    readonly inboundForeignKeysError: unknown;
    readonly onRetryInboundForeignKeys: () => void;
  };
  readonly onClose: () => void;
  readonly canQuickEdit?: boolean;
  readonly primaryKeyColumns?: string[];
  readonly onQuickEdit?: (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => void;
  /**
   * Slice 3 (FK Breadcrumb / EP3) — Open the currently-shown row in the
   * Single-Row Details View. Visible only when supplied + a PK is known.
   */
  readonly onOpenRecord?: () => void;
  /** Engine of the active connection — passed to InboundForeignKeyExplorer. */
  readonly engineId?: DatabaseEngineId;
  /** Slice 3 — when supplied, the inbound-FK "Open" button drills via the
   *  nav tree instead of opening a new TE tab. */
  readonly onDrillInboundFk?: (args: {
    foreignKey: TableExplorerInboundForeignKey;
    looseRel: LooseRelationship | undefined;
  }) => void;
};

type RowDetailsTab = 'data' | 'related-data';

export function RowDetailsPanel({
  connectionId,
  database,
  schema,
  tableName,
  rowIndex,
  row,
  columns,
  foreignKeyMap,
  layout = 'bottom',
  onSwitchLayout,
  relatedData,
  onClose,
  canQuickEdit = false,
  primaryKeyColumns = [],
  onQuickEdit,
  onOpenRecord,
  engineId,
  onDrillInboundFk,
}: RowDetailsPanelProps) {
  const [activeTab, setActiveTab] = useState<RowDetailsTab>('data');

  useEffect(() => {
    setActiveTab('data');
  }, [database, schema, tableName, rowIndex]);

  const borderClassName = layout === 'side' ? 'border-l' : 'border-t';

  return (
    <div className={`h-full flex flex-col overflow-hidden ${borderClassName} border-border bg-muted/20`} data-testid="row-details-panel">
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as RowDetailsTab)}
        className="h-full flex flex-col overflow-hidden"
      >
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-2 py-1.5">
          <TabsList className="h-7" data-testid="row-details-tabs">
            <TabsTrigger value="data" className="h-6 px-2 py-0 text-xs" data-testid="row-details-tab-data">
              Row details
            </TabsTrigger>
            <TabsTrigger
              value="related-data"
              className="h-6 px-2 py-0 text-xs"
              data-testid="row-details-tab-related-data"
            >
              Related data
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-1">
            {onOpenRecord && (
              <Button
                type="button"
                variant="default"
                size="sm"
                className="h-7 px-2 text-xs mr-1"
                onClick={onOpenRecord}
                title="Open record details (drill in)"
                data-testid="row-details-panel-open-record"
              >
                <Maximize2 className="h-3.5 w-3.5 mr-1" />
                Open record
              </Button>
            )}
            {onSwitchLayout && layout === 'bottom' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onSwitchLayout('side')}
                title="Open as side panel"
                aria-label="Open as side panel"
              >
                <PanelRightOpen className="h-4 w-4" />
              </Button>
            )}
            {onSwitchLayout && layout === 'side' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => onSwitchLayout('bottom')}
                title="Open as bottom panel"
                aria-label="Open as bottom panel"
              >
                <PanelBottomOpen className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col px-2 pb-2">
          <TabsContent
            value="data"
            className="flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden h-full"
            data-testid="row-details-content-data"
          >
            <RowDetailsDataTab
              connectionId={connectionId}
              database={database}
              row={row}
              columns={columns}
              foreignKeyMap={foreignKeyMap}
              canQuickEdit={canQuickEdit}
              primaryKeyColumns={primaryKeyColumns}
              onQuickEdit={onQuickEdit}
              sourceSchema={schema}
              sourceTable={tableName}
              engineId={engineId}
            />
          </TabsContent>

          <TabsContent
            value="related-data"
            className="flex-1 min-h-0 overflow-hidden data-[state=inactive]:hidden h-full"
            data-testid="row-details-content-related-data"
          >
            <RowDetailsRelatedDataTab
              connectionId={connectionId}
              database={database}
              schema={schema}
              tableName={tableName}
              parentRow={row}
              supportsInboundForeignKeyExplorer={relatedData.supportsInboundForeignKeyExplorer}
              inboundForeignKeys={relatedData.inboundForeignKeys}
              isInboundForeignKeysLoading={relatedData.isInboundForeignKeysLoading}
              inboundForeignKeysError={relatedData.inboundForeignKeysError}
              onRetryInboundForeignKeys={relatedData.onRetryInboundForeignKeys}
              engineId={engineId}
              onDrillInboundFk={onDrillInboundFk}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
